// Performance monitoring utilities
export class PerformanceMonitor {
  constructor() {
    this.metrics = new Map();
    this.isEnabled = process.env.NODE_ENV === 'production';
  }

  // Start timing an operation
  startTiming(name) {
    if (!this.isEnabled) return;
    
    this.metrics.set(name, {
      startTime: performance.now(),
      type: 'timing'
    });
  }

  // End timing an operation
  endTiming(name) {
    if (!this.isEnabled) return;
    
    const metric = this.metrics.get(name);
    if (metric && metric.type === 'timing') {
      const duration = performance.now() - metric.startTime;
      this.metrics.set(name, {
        ...metric,
        duration,
        endTime: performance.now()
      });
      
      // Log slow operations
      if (duration > 1000) {
        console.warn(`Slow operation detected: ${name} took ${duration.toFixed(2)}ms`);
      }
      
      return duration;
    }
  }

  // Record a counter metric
  recordEvent(name, data = {}) {
    if (!this.isEnabled) return;
    
    const existing = this.metrics.get(name);
    this.metrics.set(name, {
      type: 'counter',
      count: (existing?.count || 0) + 1,
      lastEvent: new Date().toISOString(),
      data: { ...existing?.data, ...data }
    });
  }

  // Get all metrics
  getMetrics() {
    return Object.fromEntries(this.metrics);
  }

  // Clear all metrics
  clear() {
    this.metrics.clear();
  }

  // Report metrics to backend (optional)
  async reportMetrics() {
    if (!this.isEnabled || this.metrics.size === 0) return;
    
    try {
      const metrics = this.getMetrics();
      // Here you could send metrics to your backend analytics endpoint
      console.log('Performance metrics:', metrics);
    } catch (error) {
      console.error('Failed to report metrics:', error);
    }
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// Performance measurement decorator
export function measurePerformance(target, propertyName, descriptor) {
  const method = descriptor.value;

  descriptor.value = function (...args) {
    const operationName = `${target.constructor.name}.${propertyName}`;
    performanceMonitor.startTiming(operationName);
    
    try {
      const result = method.apply(this, args);
      
      // Handle promises
      if (result && typeof result.then === 'function') {
        return result.finally(() => {
          performanceMonitor.endTiming(operationName);
        });
      }
      
      performanceMonitor.endTiming(operationName);
      return result;
    } catch (error) {
      performanceMonitor.endTiming(operationName);
      performanceMonitor.recordEvent('error', {
        operation: operationName,
        error: error.message
      });
      throw error;
    }
  };

  return descriptor;
}
